import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrashAlt, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const FileXMLUploadComponent = ({ onFilesParsed, onClearAll }) => {
  const [filesWithContent, setFilesWithContent] = useState([]);
  const [isListExpanded, setIsListExpanded] = useState(false);

  const toggleFileListVisibility = () => setIsListExpanded(prevIsListExpanded => !prevIsListExpanded); // Define toggle function here

  const parseXMLToJson = (xml) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    // Define your XML parsing logic here, similar to what you have in the "old way"
    const extractAttributes = (element) => {
      const attrs = {};
      for (let attr of element.attributes) {
        attrs[attr.nodeName] = attr.nodeValue;
      }
      return attrs;
    };

    const parseElement = (element) => {
      let obj = extractAttributes(element);
      element.childNodes.forEach(child => {
        if (child.nodeType === 1) { // Element type
          if (!obj[child.tagName]) {
            obj[child.tagName] = [parseElement(child)];
          } else {
            obj[child.tagName].push(parseElement(child));
          }
        }
      });
      return obj;
    };

    const rootElement = xmlDoc.documentElement;
    return { [rootElement.tagName]: parseElement(rootElement) };
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      // Check if the file extension is .xml
      if (file.name.toLowerCase().endsWith('.xml')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          const parsedData = parseXMLToJson(content);
          const fileWithContent = { file, content: parsedData };
          setFilesWithContent(prev => [...prev, fileWithContent]);
          onFilesParsed([fileWithContent]);
        };
        reader.readAsText(file);
      } else {
        // Optionally handle or notify the user that non-XML files were skipped
        console.log(`${file.name} is not an XML file and was skipped.`);
      }
    });
  }, [onFilesParsed]);

  const handleRemoveFile = (index) => {
    setFilesWithContent(currentFiles => currentFiles.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'application/xml, text/xml' // Only accept XML files
  });

  return (
    <div {...getRootProps()} className="bg-gray-100 p-6 cursor-pointer text-center hover:shadow rounded-lg transition-shadow duration-300 ease-in-out">
      <input {...getInputProps()} />
      <FontAwesomeIcon icon={faUpload} className="mr-2 text-gray-500" />
      <p className="text-gray-700 font-semibold text-lg">Drag 'n' drop some XML files here, or click to select files</p>
      {filesWithContent.length > 0 && (
        <div className="mt-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-gray-800">Selected Files: {filesWithContent.length}</h4>
            <button onClick={(event) => {
              event.stopPropagation(); // This stops the click event from reaching the dropzone div
              toggleFileListVisibility();
            }} className="text-blue-500 hover:text-blue-700 transition-colors duration-150">
              {isListExpanded ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
            </button>
          </div>
          {isListExpanded && (
            filesWithContent.map((fileWithContent, index) => (
              <div key={index} className="flex justify-between items-center bg-white p-3 rounded-md mb-2 shadow border">
                <span className="text-left text-gray-800 font-medium truncate">{fileWithContent.file.name}</span>
                <button onClick={(event) => { event.stopPropagation(); handleRemoveFile(index) }} className="text-red-500 hover:text-red-700 transition-colors duration-150">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            ))
          )}
          <button onClick={(event) => {
            event.stopPropagation(); // Prevent event from bubbling up
            onClearAll(); // Call the passed down function to clear files
          }} className="text-red-500 hover:text-red-700 transition-colors duration-150 mt-2">
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

export default FileXMLUploadComponent;
